import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import RadarChart from './RadarChart';
import type { GoonghapScores, GoonghapAdvice } from '@/utils/goonghapCalculations';
import { generateAdvice, getSummaryMessage } from '@/utils/goonghapCalculations';
import { generateAdviceEn, getSummaryMessageEn, SURVIVOR_TIPS_EN } from '@/utils/goonghapCalculationsEn';
import { analyzeSipseongGoonghap, type SipseongGoonghapResult } from '@/utils/sipseongGoonghap';
import { analyzeSipseongGoonghapEn } from '@/utils/sipseongGoonghapEn';
import type { SajuResult } from '@/utils/sajuCalculations';

interface GoonghapResultProps {
  nameA: string;
  nameB: string;
  scores: GoonghapScores;
  sajuA: SajuResult;
  sajuB: SajuResult;
  onRestart: () => void;
}

function ScoreRing({ score, isFakPok }: { score: number; isFakPok: boolean }) {
  const [displayed, setDisplayed] = useState(0);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 200);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!visible) return;
    let current = 0;
    const step = Math.max(1, Math.floor(score / 40));
    const interval = setInterval(() => {
      current = Math.min(current + step, score);
      setDisplayed(current);
      if (current >= score) clearInterval(interval);
    }, 30);
    return () => clearInterval(interval);
  }, [visible, score]);

  const color = isFakPok ? '#DC2626' : score >= 65 ? '#111827' : '#D97706';
  const bgColor = isFakPok ? '#FEF2F2' : score >= 65 ? '#F9FAFB' : '#FFFBEB';

  return (
    <div
      className="w-28 h-28 md:w-36 md:h-36 rounded-full flex flex-col items-center justify-center mx-auto mb-3 transition-all duration-700"
      style={{ backgroundColor: bgColor, border: `4px solid ${color}` }}
    >
      <span className="text-4xl md:text-5xl font-black leading-none" style={{ color }}>
        {displayed}
      </span>
      <span className="text-xs font-medium mt-1" style={{ color }}>/ 100</span>
    </div>
  );
}

function AdviceCard({ advice, delay }: { advice: GoonghapAdvice; delay: number }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => { const t = setTimeout(() => setVisible(true), delay); return () => clearTimeout(t); }, [delay]);

  const scoreColor = advice.score >= 68 ? '#15803D' : advice.score >= 40 ? '#D97706' : '#DC2626';
  const scoreBg    = advice.score >= 68 ? '#DCFCE7' : advice.score >= 40 ? '#FEF3C7' : '#FEE2E2';

  return (
    <div
      className="bg-white border border-gray-100 rounded-2xl p-4 transition-all duration-500"
      style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(12px)' }}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="text-lg">{advice.icon}</span>
          <span className="font-bold text-gray-900 text-sm">{advice.category}</span>
          <span className="text-xs text-gray-400 font-medium">{advice.title}</span>
        </div>
        <span
          className="text-sm font-black px-2.5 py-1 rounded-full"
          style={{ color: scoreColor, backgroundColor: scoreBg }}
        >
          {advice.score}
        </span>
      </div>
      {/* 점수 바 */}
      <div className="h-1.5 bg-gray-100 rounded-full mb-3 overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-700"
          style={{ width: visible ? `${advice.score}%` : '0%', backgroundColor: scoreColor, transitionDelay: `${delay + 200}ms` }}
        />
      </div>
      <p className="text-xs text-gray-600 leading-relaxed">{advice.advice}</p>
      {advice.tip && (
        <div className="mt-2 bg-gray-50 rounded-xl p-2.5 flex items-start gap-2">
          <div className="w-3 h-3 flex items-center justify-center flex-shrink-0 mt-0.5">
            <i className="ri-lightbulb-line text-amber-500 text-xs" />
          </div>
          <p className="text-xs text-gray-500 leading-relaxed">{advice.tip}</p>
        </div>
      )}
    </div>
  );
}

export default function GoonghapResult({ nameA, nameB, scores, sajuA, sajuB, onRestart }: GoonghapResultProps) {
  const { i18n } = useTranslation();
  const isEn = i18n.language === 'en';

  const summary = isEn
    ? getSummaryMessageEn(scores.total, nameA, nameB)
    : getSummaryMessage(scores.total, nameA, nameB);
  const adviceList = isEn ? generateAdviceEn(scores) : generateAdvice(scores);
  const isFakPok = summary.isFakPok;

  const sipseongAnalysis = isEn
    ? analyzeSipseongGoonghapEn(sajuA, sajuB, nameA, nameB)
    : analyzeSipseongGoonghap(sajuA, sajuB, nameA, nameB);

  const [activeTab, setActiveTab] = useState<'basic' | 'sipseong'>('basic');

  const survivorTips = isFakPok
    ? (isEn ? SURVIVOR_TIPS_EN : [
        '서로의 프라이버시를 극한으로 존중하세요. 매일 붙어있으면 무조건 싸워요. (주 2회 데이트 권장)',
        '싸울 때 절대 과거 이야기를 꺼내지 마세요. 이 궁합은 한 번 선을 넘으면 수습이 어려워요.',
        '큰 결정(이사, 결혼, 동업)은 만난 지 최소 2년 후에 하는 걸 추천해요.',
        '서로 다름을 교정하려 하지 말고, 그냥 다른 사람이라고 인정하세요.',
      ])
    : [];

  return (
    <div className="min-h-dvh bg-gray-50" style={{ paddingBottom: 'max(24px, env(safe-area-inset-bottom))' }}>
      {/* 헤더 */}
      <div
        className="sticky top-0 z-20 bg-white/90 backdrop-blur-md border-b border-gray-100 px-5 flex items-center justify-between"
        style={{ paddingTop: 'max(12px, env(safe-area-inset-top))', paddingBottom: '12px' }}
      >
        <img
          src="https://public.readdy.ai/ai/img_res/ff2a104a-70f3-4324-bb8d-41c2b9fa3434.png"
          alt="바른사주"
          className="h-7 object-contain"
        />
        <div className="flex items-center gap-2 bg-gray-100 rounded-full px-3 py-1.5">
          <span className="text-xs font-bold text-gray-700">{nameA}</span>
          <i className="ri-heart-3-fill text-rose-400 text-xs" />
          <span className="text-xs font-bold text-gray-700">{nameB}</span>
        </div>
      </div>

      <div className="px-4 pt-5 max-w-lg mx-auto">
        {/* 점수 섹션 */}
        <div className={`rounded-3xl p-4 md:p-6 mb-4 text-center ${isFakPok ? 'bg-red-50 border border-red-100' : 'bg-white border border-gray-100'}`}>
          <ScoreRing score={scores.total} isFakPok={isFakPok} />
          <h2 className={`text-lg font-bold leading-snug mb-2 ${isFakPok ? 'text-red-700' : 'text-gray-900'}`}>
            {summary.headline}
          </h2>
          <p className={`text-sm leading-relaxed ${isFakPok ? 'text-red-500' : 'text-gray-500'}`}>
            {summary.sub}
          </p>
        </div>

        {/* 탭 전환 */}
        <div className="bg-gray-100 rounded-xl p-1 mb-4 flex">
          <button
            onClick={() => setActiveTab('basic')}
            className={`flex-1 py-2.5 text-sm font-bold rounded-lg transition-all cursor-pointer whitespace-nowrap ${
              activeTab === 'basic' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {isEn ? 'Basic Compatibility' : '기본 궁합'}
          </button>
          <button
            onClick={() => setActiveTab('sipseong')}
            className={`flex-1 py-2.5 text-sm font-bold rounded-lg transition-all cursor-pointer whitespace-nowrap ${
              activeTab === 'sipseong' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {isEn ? 'Ten God Compatibility' : '십성 궁합'}
          </button>
        </div>

        {activeTab === 'basic' ? (
          <>
            {/* 레이더 차트 */}
            <div className="bg-white border border-gray-100 rounded-3xl p-5 mb-5">
              <h3 className="text-sm font-bold text-gray-700 mb-1">
                {isEn ? '5 Compatibility Metrics' : '5가지 궁합 능력치'}
              </h3>
              <p className="text-xs text-gray-400 mb-4">
                {isEn ? 'A wider radar means stronger compatibility' : '레이더 차트가 넓을수록 궁합이 좋아요'}
              </p>
              <RadarChart scores={scores} animate isEn={isEn} />
              {/* 점수 요약 그리드 */}
              <div className="grid grid-cols-5 gap-1.5 mt-3">
                {(isEn ? [
                  { label: 'Personality', score: scores.personality },
                  { label: 'Timing',      score: scores.timing },
                  { label: 'Wealth',      score: scores.wealth },
                  { label: 'Comm.',       score: scores.communication },
                  { label: 'Affection',   score: scores.affection },
                ] : [
                  { label: '성향',  score: scores.personality },
                  { label: '타이밍', score: scores.timing },
                  { label: '재물',  score: scores.wealth },
                  { label: '소통',  score: scores.communication },
                  { label: '애정',  score: scores.affection },
                ]).map(item => (
                  <div key={item.label} className="text-center bg-gray-50 rounded-xl py-2">
                    <p className="text-xs text-gray-400 mb-0.5">{item.label}</p>
                    <p className={`text-sm font-black ${item.score >= 68 ? 'text-gray-900' : item.score >= 40 ? 'text-amber-600' : 'text-red-500'}`}>
                      {item.score}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* 세부 조언 카드 */}
            <h3 className="text-sm font-bold text-gray-700 mb-3 px-1">
              {isEn ? 'Category Analysis' : '항목별 분석'}
            </h3>
            <div className="space-y-3 mb-5">
              {adviceList.map((advice, idx) => (
                <AdviceCard key={advice.category} advice={advice} delay={300 + idx * 120} />
              ))}
            </div>
          </>
        ) : (
          <SipseongGoonghapSection analysis={sipseongAnalysis} isEn={isEn} />
        )}

        {/* 팩폭 생존 매뉴얼 */}
        {isFakPok && (
          <div className="bg-gray-900 rounded-3xl p-5 mb-5">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 flex items-center justify-center">
                <i className="ri-alarm-warning-fill text-amber-400 text-xl" />
              </div>
              <div>
                <p className="text-white font-bold text-sm">
                  {isEn ? '🚨 Survival Manual' : '🚨 생존 매뉴얼'}
                </p>
                <p className="text-white/50 text-xs">
                  {isEn ? "Here's how to make this work" : '이 궁합, 이렇게 하면 그나마 됩니다'}
                </p>
              </div>
            </div>
            <div className="space-y-3">
              {survivorTips.map((tip, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-5 h-5 flex items-center justify-center bg-amber-400 rounded-full flex-shrink-0 mt-0.5">
                    <span className="text-gray-900 text-xs font-black">{i + 1}</span>
                  </div>
                  <p className="text-white/80 text-xs leading-relaxed">{tip}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 다시 계산 */}
        <button
          onClick={onRestart}
          className="w-full bg-gray-900 text-white font-bold text-base py-4 rounded-2xl flex items-center justify-center gap-2 hover:bg-gray-800 active:scale-95 transition-all cursor-pointer whitespace-nowrap mb-2"
        >
          <i className="ri-refresh-line text-sm" />
          {isEn ? 'Analyze Another Compatibility' : '다른 궁합 분석하기'}
        </button>
      </div>
    </div>
  );
}

// 십성 궁합 섹션
function SipseongGoonghapSection({ analysis, isEn }: { analysis: SipseongGoonghapResult; isEn: boolean }) {
  const { personA, personB, compatibility, chemistry, advice } = analysis;

  const levelConfig = {
    excellent: { color: '#10B981', bg: 'bg-emerald-50', text: 'text-emerald-700', label: isEn ? 'Excellent' : '최상' },
    good:      { color: '#6B7280', bg: 'bg-gray-50',    text: 'text-gray-700',    label: isEn ? 'Good'      : '상' },
    normal:    { color: '#6B7280', bg: 'bg-gray-50',    text: 'text-gray-700',    label: isEn ? 'Average'   : '중' },
    challenging:{ color: '#F59E0B', bg: 'bg-amber-50',  text: 'text-amber-700',   label: isEn ? 'Challenging': '하' },
  };

  const config = levelConfig[compatibility.level];

  return (
    <>
      {/* 십성 궁합 점수 */}
      <div className={`${config.bg} rounded-3xl p-5 mb-5`}>
        <div className="flex items-center justify-between mb-3">
          <span className={`text-sm font-medium ${config.text}`}>
            {isEn ? 'Ten God Compatibility Score' : '십성 궁합 점수'}
          </span>
          <span className="text-3xl font-black text-gray-900">{compatibility.score}</span>
        </div>
        <div className="h-3 bg-white/50 rounded-full overflow-hidden mb-3">
          <div
            className="h-full rounded-full transition-all duration-1000"
            style={{ width: `${compatibility.score}%`, backgroundColor: config.color }}
          />
        </div>
        <p className={`text-sm leading-relaxed ${config.text}`}>{compatibility.summary}</p>
      </div>

      {/* 두 사람의 십성 프로필 */}
      <div className="grid grid-cols-2 gap-3 mb-5">
        <div className="bg-white border border-gray-100 rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-6 h-6 bg-gray-900 text-white rounded-full flex items-center justify-center text-xs font-bold">A</span>
            <span className="text-sm font-bold text-gray-900">{personA.name}</span>
          </div>
          <div className="bg-gray-50 rounded-xl p-3 mb-2">
            <span className="text-xs text-gray-500">{isEn ? 'Day Master God' : '일간 십성'}</span>
            <p className="text-lg font-bold text-gray-900">{personA.daySipseong}</p>
          </div>
          <p className="text-xs text-gray-600 leading-relaxed">{personA.traits.nature}</p>
        </div>

        <div className="bg-white border border-gray-100 rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-6 h-6 bg-rose-500 text-white rounded-full flex items-center justify-center text-xs font-bold">B</span>
            <span className="text-sm font-bold text-gray-900">{personB.name}</span>
          </div>
          <div className="bg-rose-50 rounded-xl p-3 mb-2">
            <span className="text-xs text-rose-500">{isEn ? 'Day Master God' : '일간 십성'}</span>
            <p className="text-lg font-bold text-gray-900">{personB.daySipseong}</p>
          </div>
          <p className="text-xs text-gray-600 leading-relaxed">{personB.traits.nature}</p>
        </div>
      </div>

      {/* 화학작용 */}
      <div className="bg-white border border-gray-100 rounded-3xl p-5 mb-5">
        <h3 className="text-sm font-bold text-gray-900 mb-4">
          {isEn ? 'Chemistry Between You Two' : '두 사람의 화학작용'}
        </h3>
        <div className="space-y-3">
          {[
            { label: isEn ? 'Attraction' : '끌림/매력', value: chemistry.attraction, gradient: 'from-pink-400 to-rose-500' },
            { label: isEn ? 'Stability' : '안정성',      value: chemistry.stability,  gradient: 'from-blue-400 to-indigo-500' },
            { label: isEn ? 'Growth Potential' : '성장 가능성', value: chemistry.growth, gradient: 'from-emerald-400 to-green-500' },
          ].map(item => (
            <div key={item.label}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-gray-500">{item.label}</span>
                <span className="text-sm font-bold text-gray-900">{item.value}%</span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className={`h-full bg-gradient-to-r ${item.gradient} rounded-full transition-all duration-1000`}
                  style={{ width: `${item.value}%` }}
                />
              </div>
            </div>
          ))}
        </div>
        <p className="text-sm text-gray-600 mt-4 leading-relaxed">{chemistry.description}</p>
      </div>

      {/* 연애 스타일 비교 */}
      <div className="bg-white border border-gray-100 rounded-3xl p-5 mb-5">
        <h3 className="text-sm font-bold text-gray-900 mb-4">
          {isEn ? 'Love Style' : '연애 스타일'}
        </h3>
        <div className="space-y-3">
          <div className="flex items-start gap-3">
            <span className="w-6 h-6 bg-gray-900 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">A</span>
            <div>
              <p className="text-sm font-medium text-gray-900">{personA.name}</p>
              <p className="text-xs text-gray-600 mt-0.5">{personA.traits.loveStyle}</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <span className="w-6 h-6 bg-rose-500 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">B</span>
            <div>
              <p className="text-sm font-medium text-gray-900">{personB.name}</p>
              <p className="text-xs text-gray-600 mt-0.5">{personB.traits.loveStyle}</p>
            </div>
          </div>
        </div>
      </div>

      {/* 조언 */}
      <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl p-5 mb-5">
        <h3 className="text-sm font-bold text-white mb-4">
          {isEn ? 'Ten God Compatibility Advice' : '십성 궁합 조언'}
        </h3>
        <div className="space-y-3">
          <div className="flex items-start gap-3">
            <div className="w-5 h-5 bg-emerald-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-check-line text-white text-xs" />
            </div>
            <div>
              <p className="text-xs text-emerald-400 font-medium mb-0.5">{isEn ? 'Strength' : '강점'}</p>
              <p className="text-sm text-white/90 leading-relaxed">{advice.strongPoint}</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-5 h-5 bg-amber-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-alert-line text-white text-xs" />
            </div>
            <div>
              <p className="text-xs text-amber-400 font-medium mb-0.5">{isEn ? 'Watch Out' : '주의할 점'}</p>
              <p className="text-sm text-white/90 leading-relaxed">{advice.watchOut}</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-lightbulb-line text-white text-xs" />
            </div>
            <div>
              <p className="text-xs text-blue-400 font-medium mb-0.5">{isEn ? 'Tip' : '팁'}</p>
              <p className="text-sm text-white/90 leading-relaxed">{advice.tip}</p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
